import React from 'react';
import {
  ChartBar, Percent, CaretDown, Minus, Plus, Coins, Bank, PaperPlaneTilt,
  Warning, CheckCircle, CaretRight, Lightbulb, Sparkle
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';

/**
 * Pricing tab for AI Pricing Tool — cost breakdown, profit margin calculator,
 * permit info, factors & recommendations, AI photo observations.
 *
 * Fully stateless. Receives all data + helpers as explicit props from parent.
 */
export default function PricingTab({
  displayEstimate,
  selectedTier,
  showProfitCalculator,
  setShowProfitCalculator,
  profitMargin,
  setProfitMargin,
  applyProfitMargin,
  calculateProfit,
  setShowQuoteModal,
  photos,
}) {
  if (!displayEstimate) return null;

  return (
    <div className="space-y-6">
      {/* Cost Breakdown */}
      <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F]">
        <div className="p-4 border-b-2 border-black dark:border-white bg-neutral-50 dark:bg-neutral-900">
          <h4 className="font-black text-neutral-900 dark:text-white uppercase tracking-wider text-sm flex items-center gap-2">
            <ChartBar size={18} weight="bold" className="text-[#2B4C6F]" />
            COST BREAKDOWN
          </h4>
        </div>
        <div className="p-5">
          <div className="space-y-4">
            {Object.entries(displayEstimate.breakdown || {}).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 border-2 border-black dark:border-white"
                    style={{ backgroundColor: `rgba(91, 143, 185, ${(value.percentage || 0) / 100 + 0.2})` }}
                  />
                  <span className="text-neutral-700 dark:text-neutral-300 uppercase tracking-wide text-sm font-medium">
                    {key.replace('_', ' ')}
                  </span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-neutral-900 dark:text-white font-mono">{value.estimate}</span>
                  <span className="text-sm text-neutral-500 ml-2 font-mono">({value.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex h-4 mt-6 border-2 border-black dark:border-white overflow-hidden">
            <div className="bg-[#2B4C6F]" style={{ width: `${displayEstimate.breakdown?.labor?.percentage || 0}%` }} />
            <div className="bg-[#7BA7C7]" style={{ width: `${displayEstimate.breakdown?.materials?.percentage || 0}%` }} />
            <div className="bg-[#9BBDD7]" style={{ width: `${displayEstimate.breakdown?.permits_fees?.percentage || 0}%` }} />
            <div className="bg-[#BCD3E7]" style={{ width: `${displayEstimate.breakdown?.contingency?.percentage || 0}%` }} />
          </div>
        </div>
      </div>

      {/* Profit Margin Calculator */}
      <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F]">
        <button
          onClick={() => setShowProfitCalculator(!showProfitCalculator)}
          className="w-full p-4 border-b-2 border-black dark:border-white bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-between hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
          data-testid="profit-calculator-toggle"
        >
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-emerald-500 border-2 border-black flex items-center justify-center">
              <Percent size={20} weight="bold" className="text-white" />
            </div>
            <div className="text-left">
              <h4 className="font-black text-neutral-900 dark:text-white uppercase tracking-wider text-sm">
                PROFIT MARGIN CALCULATOR
              </h4>
              <p className="text-xs text-neutral-500">
                {profitMargin > 0 ? `${profitMargin}% markup applied` : 'Add your desired profit margin'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {profitMargin > 0 && (
              <span className="px-3 py-1 bg-emerald-500 text-white text-sm font-bold border-2 border-black">
                +${calculateProfit(displayEstimate.tiers?.[selectedTier]?.total || 0).toLocaleString()}
              </span>
            )}
            <CaretDown
              size={20}
              weight="bold"
              className={`text-neutral-600 transition-transform ${showProfitCalculator ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        {showProfitCalculator && (
          <div className="p-5 space-y-6" data-testid="profit-calculator-content">
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs">
                  PROFIT MARGIN
                </Label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setProfitMargin(Math.max(0, profitMargin - 5))}
                    className="w-8 h-8 bg-neutral-200 dark:bg-neutral-700 border-2 border-black dark:border-white flex items-center justify-center hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-colors"
                    data-testid="margin-decrease"
                  >
                    <Minus size={16} weight="bold" />
                  </button>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={profitMargin}
                      onChange={(e) => setProfitMargin(Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                      className="w-20 h-10 text-center text-xl font-black font-mono border-2 border-black dark:border-white bg-white dark:bg-neutral-900 rounded-none pr-6"
                      data-testid="margin-input"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 font-bold">%</span>
                  </div>
                  <button
                    onClick={() => setProfitMargin(Math.min(100, profitMargin + 5))}
                    className="w-8 h-8 bg-neutral-200 dark:bg-neutral-700 border-2 border-black dark:border-white flex items-center justify-center hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-colors"
                    data-testid="margin-increase"
                  >
                    <Plus size={16} weight="bold" />
                  </button>
                </div>
              </div>

              <div className="relative pt-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={profitMargin}
                  onChange={(e) => setProfitMargin(parseInt(e.target.value, 10))}
                  className="w-full h-3 bg-neutral-200 dark:bg-neutral-700 border-2 border-black dark:border-white appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:bg-emerald-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:cursor-pointer"
                  data-testid="margin-slider"
                />
                <div className="flex justify-between mt-1 text-xs text-neutral-500 font-mono">
                  <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {[10, 15, 20, 25, 30, 35].map((preset) => (
                  <button
                    key={preset}
                    onClick={() => setProfitMargin(preset)}
                    className={`px-4 py-2 font-bold text-sm border-2 border-black dark:border-white transition-all ${
                      profitMargin === preset
                        ? 'bg-emerald-500 text-white shadow-[2px_2px_0px_#000]'
                        : 'bg-white dark:bg-neutral-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
                    }`}
                    data-testid={`preset-${preset}`}
                  >
                    {preset}%
                  </button>
                ))}
              </div>
            </div>

            {displayEstimate.tiers && (
              <div className="border-t-2 border-black dark:border-white pt-5">
                <h5 className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs mb-4 flex items-center gap-2">
                  <Coins size={16} weight="bold" className="text-emerald-500" />
                  PRICING WITH {profitMargin}% MARGIN
                </h5>

                <div className="grid md:grid-cols-3 gap-4">
                  {['economy', 'standard', 'premium'].map((tierKey) => {
                    const tier = displayEstimate.tiers[tierKey];
                    const basePrice = tier?.total || 0;
                    const withMargin = applyProfitMargin(basePrice);
                    const profit = calculateProfit(basePrice);
                    const isSelected = selectedTier === tierKey;

                    return (
                      <div
                        key={tierKey}
                        className={`p-4 border-2 ${isSelected ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-neutral-300 dark:border-neutral-600'}`}
                      >
                        <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">
                          {tier?.name || tierKey}
                        </p>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-neutral-500">Base:</span>
                          <span className="font-mono text-neutral-700 dark:text-neutral-300">
                            ${basePrice.toLocaleString()}
                          </span>
                        </div>
                        {profitMargin > 0 && (
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-emerald-600">+ Profit:</span>
                            <span className="font-mono text-emerald-600 font-bold">
                              +${profit.toLocaleString()}
                            </span>
                          </div>
                        )}
                        <div className="border-t-2 border-dashed border-neutral-300 dark:border-neutral-600 pt-2 mt-2">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-neutral-900 dark:text-white text-sm">QUOTE:</span>
                            <span className={`font-mono font-black text-xl ${isSelected ? 'text-emerald-600' : 'text-neutral-900 dark:text-white'}`}>
                              ${withMargin.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {profitMargin > 0 && (
                  <div className="mt-4 p-4 bg-emerald-500 border-2 border-black shadow-[4px_4px_0px_#000]">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 text-white">
                      <div className="flex items-center gap-3">
                        <Bank size={24} weight="bold" />
                        <div>
                          <p className="text-emerald-100 text-xs uppercase tracking-wider">
                            Your profit on {displayEstimate.tiers[selectedTier]?.name || selectedTier}
                          </p>
                          <p className="font-black text-2xl font-mono">
                            ${calculateProfit(displayEstimate.tiers[selectedTier]?.total || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-emerald-100 text-xs uppercase tracking-wider">Quote to customer</p>
                          <p className="font-black text-2xl font-mono">
                            ${applyProfitMargin(displayEstimate.tiers[selectedTier]?.total || 0).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          onClick={() => setShowQuoteModal(true)}
                          className="h-12 px-5 bg-white text-emerald-600 hover:bg-emerald-50 font-black uppercase tracking-wider text-sm border-2 border-black rounded-none shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                          data-testid="apply-margin-send-quote"
                        >
                          <PaperPlaneTilt size={18} weight="fill" className="mr-2" />
                          Send Quote
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {profitMargin > 0 && (
              <button
                onClick={() => setProfitMargin(0)}
                className="w-full py-2 text-sm font-bold text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 uppercase tracking-wider transition-colors"
                data-testid="reset-margin"
              >
                Reset to base prices
              </button>
            )}
          </div>
        )}
      </div>

      {/* Permit Info */}
      {displayEstimate.permits_required && (
        <div className={`border-2 p-4 flex items-start gap-3 ${
          displayEstimate.permits_required.likely_required
            ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
            : 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
        }`}>
          {displayEstimate.permits_required.likely_required ? (
            <Warning size={24} weight="fill" className="text-amber-500 flex-shrink-0" />
          ) : (
            <CheckCircle size={24} weight="fill" className="text-emerald-500 flex-shrink-0" />
          )}
          <div>
            <p className="font-bold text-neutral-900 dark:text-white">
              {displayEstimate.permits_required.likely_required ? 'Permits Likely Required' : 'Permits Unlikely'}
            </p>
            {displayEstimate.permits_required.permit_types?.length > 0 && (
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                Types: {displayEstimate.permits_required.permit_types.join(', ')}
              </p>
            )}
            {displayEstimate.permits_required.estimated_cost && (
              <p className="text-sm font-mono text-neutral-700 dark:text-neutral-300 mt-1">
                Est. Cost: ${displayEstimate.permits_required.estimated_cost}
              </p>
            )}
            <p className="text-xs text-neutral-500 mt-2">{displayEstimate.permits_required.notes}</p>
          </div>
        </div>
      )}

      {/* Factors & Recommendations */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F]">
          <div className="p-4 border-b-2 border-black dark:border-white bg-amber-50 dark:bg-amber-900/20">
            <h4 className="font-black text-neutral-900 dark:text-white uppercase tracking-wider text-sm flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-amber-500" />
              PRICE FACTORS
            </h4>
          </div>
          <ul className="p-5 space-y-3">
            {displayEstimate.factors_affecting_price?.map((factor, idx) => (
              <li key={`factor-${idx}`} className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                <CaretRight size={14} weight="bold" className="text-amber-500 mt-1 flex-shrink-0" />
                {factor}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F]">
          <div className="p-4 border-b-2 border-black dark:border-white bg-emerald-50 dark:bg-emerald-900/20">
            <h4 className="font-black text-neutral-900 dark:text-white uppercase tracking-wider text-sm flex items-center gap-2">
              <Lightbulb size={18} weight="fill" className="text-emerald-500" />
              RECOMMENDATIONS
            </h4>
          </div>
          <ul className="p-5 space-y-3">
            {displayEstimate.recommendations?.map((rec, idx) => (
              <li key={`rec-${idx}`} className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                <CheckCircle size={14} weight="fill" className="text-emerald-500 mt-1 flex-shrink-0" />
                {rec}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* AI Photo Observations — only when photos were analyzed */}
      {displayEstimate._photos_analyzed > 0 && (
        <div
          className="bg-white dark:bg-[#0F172A] border-2 border-[#2B4C6F] shadow-[4px_4px_0px_0px_#2B4C6F]"
          data-testid="photo-observations-panel"
        >
          <div className="p-4 border-b-2 border-[#2B4C6F] bg-[#2B4C6F]/10 flex items-center justify-between">
            <h4 className="font-black text-neutral-900 dark:text-white uppercase tracking-wider text-sm flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-[#2B4C6F]" />
              AI VISION ANALYSIS
            </h4>
            <span className="text-xs font-mono text-[#2B4C6F] font-bold">
              {displayEstimate._photos_analyzed} PHOTO{displayEstimate._photos_analyzed === 1 ? '' : 'S'} ANALYZED
            </span>
          </div>
          <div className="p-5 space-y-4">
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="photo-observations-thumbs">
                {photos.map((p) => (
                  <a
                    key={p.file_id}
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block w-20 h-20 border-2 border-black dark:border-white bg-neutral-100 dark:bg-neutral-900 overflow-hidden"
                  >
                    <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
            {displayEstimate.photo_observations?.length > 0 ? (
              <ul className="space-y-3">
                {displayEstimate.photo_observations.map((obs, idx) => (
                  <li
                    key={`obs-${idx}`}
                    className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300"
                    data-testid={`photo-observation-${idx}`}
                  >
                    <CaretRight size={14} weight="bold" className="text-[#2B4C6F] mt-1 flex-shrink-0" />
                    {obs}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-neutral-500 italic">
                No specific observations were extracted from the photos.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
